import { Link, useNavigate } from "react-router";
import { motion } from "motion/react";
import { ArrowLeft } from "lucide-react";
import {
  LandingNavbar,
  NAVBAR_HEIGHT,
} from "@/components/landing/LandingNavbar";
import { Footer } from "@/components/landing/Footer";
import { FloatingSubscribe } from "@/components/landing/PulseSubscribeBar";
import type { DailyPulse, PulseStory } from "@altair/shared/types";
import { resolveStorySlug } from "@/lib/news/slug";
import { StoryBlock } from "./StoryBlock";
import { formatPulseDate } from "./payer-colors";

/**
 * Single-story deep-link view for `/news/:date/:slug`. Renders ONLY the
 * focused story plus a back link to the parent pulse — distinct from
 * FullPulseView's full-list render. Reuses <StoryBlock /> so the story card
 * markup matches the list surface exactly.
 */

interface SingleStoryViewProps {
  pulse: DailyPulse;
  story: PulseStory;
}

export function SingleStoryView({ pulse, story }: SingleStoryViewProps) {
  const navigate = useNavigate();
  const navbarHeight = NAVBAR_HEIGHT;
  const slug = resolveStorySlug(story);
  const indexInPulse = pulse.stories.findIndex(
    (s) => resolveStorySlug(s) === slug
  );

  return (
    <div className="min-h-dvh">
      <LandingNavbar
        isProduct={false}
        onBack={() => {
          // Force a full-page nav so / loads from the fresh main-webapp
          // deploy. /news is served by a separate news-renderer SSR
          // service that references an older bundle, and SPA-navigating
          // back to / would reuse that stale JS to render the old
          // landing page (e.g. pre-launch countdown navbar).
          window.location.href = "/";
        }}
      />

      <main
        className="px-5"
        style={{
          paddingTop: `max(${navbarHeight + 40}px, 12vh)`,
          paddingBottom: 96,
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 680 }}>
          {/* Back link — returns to the parent pulse's full-list view.
              reloadDocument → full page load so the news-renderer SSRs the
              pulse fresh; client-side routing would reuse this story's payload
              (no sibling stories) and mis-render. */}
          <Link
            to={`/news/${pulse.date}`}
            reloadDocument
            className="inline-flex items-center gap-1.5 no-underline mb-8"
            style={{
              fontSize: 13,
              color: "hsl(var(--foreground) / 0.40)",
              transition: "color 150ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground) / 0.40)";
            }}
          >
            <ArrowLeft size={14} />
            Back to {formatPulseDate(pulse.date)}
          </Link>

          <motion.header
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: "hsl(var(--foreground) / 0.45)",
                letterSpacing: "0.01em",
                textTransform: "uppercase",
              }}
            >
              Billing News · {formatPulseDate(pulse.date)}
            </div>
            <div
              style={{
                borderTop: "1px solid hsl(var(--foreground) / 0.08)",
                marginTop: 16,
              }}
            />
          </motion.header>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ marginTop: 36 }}
          >
            <StoryBlock
              story={story}
              date={pulse.date}
              slug={slug}
              index={indexInPulse < 0 ? 0 : indexInPulse}
              headingLevel="h1"
            />
          </motion.div>

          <div
            style={{
              marginTop: 56,
              paddingTop: 24,
              borderTop: "1px solid hsl(var(--foreground) / 0.08)",
            }}
          >
            <Link
              to={`/news/${pulse.date}`}
              reloadDocument
              className="no-underline"
              style={{
                fontSize: 13,
                color: "hsl(var(--foreground) / 0.45)",
                transition: "color 150ms",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "hsl(var(--foreground))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "hsl(var(--foreground) / 0.45)";
              }}
            >
              ← All stories from {formatPulseDate(pulse.date)}
            </Link>
          </div>
        </div>
      </main>

      <Footer />

      <FloatingSubscribe />
    </div>
  );
}
